import { useState, useEffect, type FormEvent, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { authApi, cloudApi } from '../services/api';
import { isMasterSandboxUser } from '../lib/testOverride';
import { cancelVoicePreview } from '../lib/voiceProfile';
import {
  GENDER_OPTIONS,
  LANGUAGE_OPTIONS,
  WEATHER_OPTIONS,
  formatVoiceProfileLabel,
  resolveVoiceFromMatrix,
  previewVoiceSample,
} from '../lib/voiceProfile';
import {
  buildCloudStorageMeta,
  isGoogleCloudProvider,
  normalizeCloudProvider,
  type CloudProviderValue,
  type CloudStorageMeta,
} from '../lib/cloudStorage';
import {
  DELETE_ACCOUNT_SUCCESS_MESSAGE,
  DELETE_ACCOUNT_WARNING,
} from '../lib/deleteAccount';
import { SCREENS } from '../lib/screenRegistry';
import { isMaintenancePreviewEmbed } from '../lib/previewEnvironment';
import { rememberSetupDisplayName } from '../components/LivingRoomConsole';
import FileStoragePanel from '../components/FileStoragePanel';
import BusinessContextPanel from '../components/BusinessContextPanel';
import './ProSetup.css';

const LOGO = encodeURI('/Assets/logo/Mentology Logo.png');

const PRO_TEST_DEFAULTS = {
  name: 'John Citizen',
  preferred_name: 'John',
  address: '123 Ocean Drive, Noosa Heads QLD 4567',
  postcode: '4567',
  country: 'Australia',
  weather: '°C',
  language_target: '',
  ai_mentor_gender: '',
  cloud_provider: 'pending' as CloudProviderValue,
  cloud_folder_label: '',
  cloud_folder_id: null as string | null,
  cloud_connected: false,
};

type SetupForm = typeof PRO_TEST_DEFAULTS;

const EMPTY_DEFAULTS: SetupForm = {
  name: '',
  preferred_name: '',
  address: '',
  postcode: '',
  country: '',
  weather: '°C',
  language_target: '',
  ai_mentor_gender: '',
  cloud_provider: 'pending',
  cloud_folder_label: '',
  cloud_folder_id: null,
  cloud_connected: false,
};

function bioToForm(bio: Record<string, unknown> | null | undefined): SetupForm {
  const cloud = (bio?.cloud_storage && typeof bio.cloud_storage === 'object')
    ? (bio.cloud_storage as Record<string, unknown>)
    : {};
  const meta = buildCloudStorageMeta(cloud);
  return {
    ...EMPTY_DEFAULTS,
    name: String(bio?.name ?? ''),
    preferred_name: String(bio?.preferred_name ?? ''),
    address: String(bio?.address ?? ''),
    postcode: String(bio?.postcode ?? ''),
    country: String(bio?.country ?? ''),
    weather: String(bio?.weather || '°C'),
    language_target: String(bio?.language_target ?? ''),
    ai_mentor_gender: String(bio?.ai_mentor_gender ?? ''),
    cloud_provider: meta.provider,
    cloud_folder_label: meta.folder_label,
    cloud_folder_id: meta.folder_id || null,
    cloud_connected: Boolean(meta.connected),
  };
}

export default function ProSetup() {
  const navigate = useNavigate();
  const { user, masterSandboxUnlocked, logout } = useAuth();
  const [form, setForm] = useState<SetupForm>(EMPTY_DEFAULTS);
  const [loading, setLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [cloudStatusNote, setCloudStatusNote] = useState('');

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      cancelVoicePreview();
    };
  }, []);

  useEffect(() => {
    if (window.self === window.top) return undefined;

    document.documentElement.classList.add('maintenance-embed-preview');
    document.body.classList.add('maintenance-embed-preview');
    document.getElementById('root')?.classList.add('maintenance-embed-preview');

    return () => {
      document.documentElement.classList.remove('maintenance-embed-preview');
      document.body.classList.remove('maintenance-embed-preview');
      document.getElementById('root')?.classList.remove('maintenance-embed-preview');
    };
  }, []);

  useEffect(() => {
    if (isMaintenancePreviewEmbed()) {
      setForm({ ...EMPTY_DEFAULTS });
      return;
    }

    authApi.getBio()
      .then((data) => {
        if (data.bio) {
          setForm(bioToForm(data.bio));
          rememberSetupDisplayName({
            preferred_name: String(data.bio.preferred_name ?? ''),
            name: String(data.bio.name ?? ''),
          });
        } else if (isMasterSandboxUser(user) || masterSandboxUnlocked) {
          setForm({ ...PRO_TEST_DEFAULTS });
          rememberSetupDisplayName(PRO_TEST_DEFAULTS);
        }
      })
      .catch(() => {
        if (isMasterSandboxUser(user) || masterSandboxUnlocked) {
          setForm({ ...PRO_TEST_DEFAULTS });
          rememberSetupDisplayName(PRO_TEST_DEFAULTS);
        }
      })
      .finally(() => {
        cloudApi.status('user')
          .then((data) => {
            const meta = buildCloudStorageMeta(data.cloud_storage || {});
            setForm((current) => ({
              ...current,
              cloud_provider: meta.provider,
              cloud_folder_label: meta.folder_label || current.cloud_folder_label,
              cloud_folder_id: meta.folder_id || null,
              cloud_connected: Boolean(meta.connected),
            }));
          })
          .catch(() => {});
      });

    const params = new URLSearchParams(window.location.search);
    const cloudFlag = params.get('cloud');
    if (cloudFlag === 'connected') setCloudStatusNote('Google Drive connected.');
    if (cloudFlag === 'denied') setCloudStatusNote('Google authorization denied.');
    if (cloudFlag === 'error' || cloudFlag === 'invalid_state') {
      setCloudStatusNote('Google connection failed. Try again.');
    }
    if (cloudFlag) {
      params.delete('cloud');
      window.history.replaceState({}, '', `${window.location.pathname}${params.toString() ? `?${params}` : ''}`);
    }
  }, [user, masterSandboxUnlocked]);

  const voiceEntry = resolveVoiceFromMatrix(form.language_target, form.ai_mentor_gender);
  const canDelete = deleteConfirm.trim() === 'DELETE';

  const update = (field: keyof SetupForm, value: string) => {
    setPreviewError('');
    setForm((current) => ({ ...current, [field]: value }));
  };

  const preventFormSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const blockEnterSubmit = (event: React.KeyboardEvent<HTMLFormElement>) => {
    if (event.key !== 'Enter') return;
    const target = event.target as HTMLElement;
    if (target.tagName === 'BUTTON') return;
    event.preventDefault();
    event.stopPropagation();
  };

  const handlePreview = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!voiceEntry || previewLoading) return;

    setPreviewLoading(true);
    setPreviewError('');

    try {
      await previewVoiceSample(voiceEntry.voiceId, voiceEntry.name, {
        language: voiceEntry.language,
        gender: voiceEntry.gender,
        modelId: voiceEntry.modelId,
      });
    } catch (error) {
      console.error('[Voice preview]', error);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleSave = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (loading) return;

    setLoading(true);
    cancelVoicePreview();

    const entry = resolveVoiceFromMatrix(form.language_target, form.ai_mentor_gender);
    const cloud_storage = buildCloudStorageMeta({
      provider: normalizeCloudProvider(form.cloud_provider),
      folder_label: form.cloud_folder_label.trim(),
      folder_id: form.cloud_folder_id,
      connected: form.cloud_connected,
    });

    let liveCloud = cloud_storage;
    try {
      const data = await cloudApi.status('user');
      const live = buildCloudStorageMeta(data.cloud_storage || {});
      if (live.connected || live.folder_id) liveCloud = live;
    } catch {
      liveCloud = cloud_storage;
    }

    const payload = {
      name: form.name,
      preferred_name: form.preferred_name,
      address: form.address,
      postcode: form.postcode,
      country: form.country,
      weather: form.weather,
      language_target: form.language_target || null,
      ai_mentor_gender: form.ai_mentor_gender || null,
      ai_mentor_name: entry?.name ?? null,
      ai_mentor_voice_id: entry?.voiceId ?? null,
      cloud_storage: liveCloud,
    };

    try {
      await authApi.saveBio(payload);
      rememberSetupDisplayName({
        preferred_name: form.preferred_name,
        name: form.name,
      });
      sessionStorage.removeItem('mentology.pendingBio');
    } catch (error) {
      sessionStorage.setItem('mentology.pendingBio', JSON.stringify(payload));
      rememberSetupDisplayName({
        preferred_name: form.preferred_name,
        name: form.name,
      });
      console.warn('[ProSetup] Bio save issue — continuing onboarding', error);
    }

    setLoading(false);
    navigate('/workspace', { replace: true });
  };

  const persistSetupCloud = async (meta: CloudStorageMeta) => {
    setForm((current) => ({
      ...current,
      cloud_provider: meta.provider,
      cloud_folder_label: meta.folder_label,
      cloud_folder_id: meta.folder_id || null,
      cloud_connected: Boolean(meta.connected),
    }));
    const entry = resolveVoiceFromMatrix(form.language_target, form.ai_mentor_gender);
    try {
      await authApi.saveBio({
        name: form.name,
        preferred_name: form.preferred_name,
        address: form.address,
        postcode: form.postcode,
        country: form.country,
        weather: form.weather,
        language_target: form.language_target || null,
        ai_mentor_gender: form.ai_mentor_gender || null,
        ai_mentor_name: entry?.name ?? null,
        ai_mentor_voice_id: entry?.voiceId ?? null,
        cloud_storage: meta,
      });
    } catch {
      // OAuth callback still persists the live Drive folder if Connect continues.
    }
  };

  const handleReviewSetup = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    cancelVoicePreview();
    setForm({ ...EMPTY_DEFAULTS });
    setDeleteConfirm('');
    setDeleteError('');
    setPreviewError('');
    sessionStorage.removeItem('mentology.pendingBio');
  };

  const handleCancelSubscription = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    alert('Subscription cancellation will take effect at the end of the current billing window.');
  };

  const handleDeleteAccount = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!canDelete || deleteLoading) return;

    setDeleteLoading(true);
    setDeleteError('');
    try {
      const result = await authApi.deleteData();
      if (!result.success) {
        setDeleteError(result.message || result.reason || 'Delete failed');
        setDeleteLoading(false);
        return;
      }
      alert(result.message || DELETE_ACCOUNT_SUCCESS_MESSAGE);
      await logout();
      navigate('/', { replace: true });
    } catch (error: unknown) {
      const err = error as { error?: string; message?: string };
      setDeleteError(err.error || err.message || 'Delete failed');
      setDeleteLoading(false);
    }
  };

  return (
    <div className="pro-setup-screen" data-screen-id={SCREENS.PRO04}>
      <div className="pro-setup-logo-wrap">
        <img
          src={LOGO}
          alt="Mentology"
          className="pro-setup-logo brand-logo"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      </div>

      <form
        className="pro-setup-form"
        onSubmit={preventFormSubmit}
        onKeyDown={blockEnterSubmit}
        noValidate
      >
      <div className="pro-setup-grid-wrap">
        <div className="pro-setup-grid">
          <label className="pro-setup-label scannable-bullet-text">
            Name: First and Surname
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Preferred Name: (what would you like me to call you)
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.preferred_name}
              onChange={(e) => update('preferred_name', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Address:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Postcode:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.postcode}
              onChange={(e) => update('postcode', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Country:
            <input
              className="input-field pro-setup-input"
              type="text"
              value={form.country}
              onChange={(e) => update('country', e.target.value)}
            />
          </label>

          <label className="pro-setup-label scannable-bullet-text">
            Weather:
            <select
              className="input-field pro-setup-input pro-setup-select"
              value={form.weather}
              onChange={(e) => update('weather', e.target.value)}
            >
              {WEATHER_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>

          <div className="pro-setup-row-full">
            <div className="pro-setup-voice-row">
              <label className="pro-setup-label scannable-bullet-text">
                Preferred Language
                <select
                  className="input-field pro-setup-input pro-setup-select"
                  value={form.language_target}
                  onChange={(e) => update('language_target', e.target.value)}
                >
                  <option value="">Select language</option>
                  {LANGUAGE_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </label>

              <div className="pro-setup-voice-cell">
                <label className="pro-setup-label scannable-bullet-text">
                  AI-Mentor Voice
                  <select
                    className="input-field pro-setup-input pro-setup-select"
                    value={form.ai_mentor_gender}
                    onChange={(e) => update('ai_mentor_gender', e.target.value)}
                  >
                    <option value="">Select gender</option>
                    {GENDER_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </label>

                {voiceEntry && (
                  <p className="pro-setup-voice-profile-line">
                    <span className="pro-setup-mentor-name">
                      Voice Profile: {formatVoiceProfileLabel(voiceEntry)}
                    </span>
                    {' '}
                    <button
                      type="button"
                      className="pro-setup-voice-preview-link"
                      onClick={handlePreview}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                      }}
                      disabled={previewLoading}
                      aria-label="Play voice preview sample"
                    >
                      {previewLoading ? '[ ► loading... ]' : '[ ► tap to hear ]'}
                    </button>
                    {previewError && (
                      <span className="pro-setup-voice-preview-error">{previewError}</span>
                    )}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="pro-setup-row-full pro-setup-systems">
            <h2 className="pro-setup-section-heading">Grant access to systems</h2>
            <FileStoragePanel
              storageKey="pro-setup"
              scope="user"
              returnTo="/pro-setup"
              defaultOpen
              compact
              onPersist={persistSetupCloud}
              onBeforeGoogleConnect={async () => {
                const provider = isGoogleCloudProvider(form.cloud_provider)
                  ? form.cloud_provider
                  : 'google_drive';
                await persistSetupCloud(buildCloudStorageMeta({
                  provider,
                  folder_label: form.cloud_folder_label,
                  folder_id: form.cloud_folder_id,
                  connected: form.cloud_connected,
                }));
              }}
            />
            {cloudStatusNote && <p className="pro-setup-helper-line">{cloudStatusNote}</p>}

            <BusinessContextPanel
              scope="user"
              cloud={buildCloudStorageMeta({
                provider: form.cloud_provider,
                folder_label: form.cloud_folder_label,
                folder_id: form.cloud_folder_id,
                connected: form.cloud_connected,
              })}
            />
          </div>

          <div className="pro-setup-row-full pro-setup-delete">
            <h2 className="pro-setup-section-heading">Cancel subscription &amp; Delete account</h2>
            <p className="pro-setup-delete-warning">{DELETE_ACCOUNT_WARNING}</p>
            <button
              type="button"
              className="btn-primary pro-setup-cancel-sub-btn"
              onClick={handleCancelSubscription}
            >
              Cancel subscription
            </button>
            <label className="pro-setup-label scannable-bullet-text">
              Type DELETE to confirm
              <input
                className="input-field pro-setup-input"
                type="text"
                value={deleteConfirm}
                placeholder="DELETE"
                autoComplete="off"
                onChange={(e) => {
                  setDeleteConfirm(e.target.value);
                  setDeleteError('');
                }}
              />
            </label>
            {deleteError && (
              <p className="pro-setup-voice-preview-error">{deleteError}</p>
            )}
            <button
              type="button"
              className="btn-primary pro-setup-delete-btn"
              onClick={handleDeleteAccount}
              disabled={!canDelete || deleteLoading}
            >
              {deleteLoading ? 'Deleting...' : 'Delete account'}
            </button>
          </div>
        </div>
      </div>

      <div className="pro-setup-action pro-setup-action-row">
        <button
          type="button"
          className="btn-primary pro-setup-review-btn"
          onClick={handleReviewSetup}
        >
          Review Setup — Reset all fields
        </button>
        <button
          type="button"
          className="btn-primary pro-setup-save-btn"
          onClick={handleSave}
          disabled={loading}
        >
          {loading ? 'Saving...' : 'SAVE & ENTER BRIEFING ROOM'}
        </button>
      </div>
      </form>
    </div>
  );
}
